import React, { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Check, CaretUpDown, X, MagnifyingGlass } from "@phosphor-icons/react";

/**
 * Searchable combobox dropdown.
 *
 *  - `options` is `[{ value: string, label: string, sub?: string }]`
 *  - `value` is the selected `value` (or "")
 *  - `onChange(newValue)` fires when user picks; pass "" to clear
 *  - `placeholder`, `emptyText`, `clearable`, `width`, `className`,
 *    `testId`, `allLabel` are all optional cosmetics.
 *
 * Designed to drop straight into existing Select sites — same width,
 * same vertical rhythm, same hover/focus colours as our Radix Select.
 */
const SearchSelect = ({
  options,
  value,
  onChange,
  placeholder = "Choose…",
  allLabel = null, // when set, shows a top "All" item that clears the value
  emptyText = "No matches",
  width = "w-[200px]",
  height = "h-9",
  testId,
  disabled = false,
  className = "",
}) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const list = useMemo(() => {
    if (!q.trim()) return options;
    const needle = q.trim().toLowerCase();
    return options.filter(
      (o) =>
        (o.label || "").toLowerCase().includes(needle) ||
        (o.value || "").toString().toLowerCase().includes(needle) ||
        (o.sub || "").toLowerCase().includes(needle),
    );
  }, [options, q]);

  const selected = options.find((o) => String(o.value) === String(value));

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setQ(""); }}>
      <PopoverTrigger asChild disabled={disabled}>
        <button
          type="button"
          data-testid={testId}
          className={`${width} ${height} ${className} inline-flex items-center justify-between gap-2 px-3 py-2 text-sm border border-border rounded-sm bg-white hover:border-myb-500 focus:border-myb-500 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
        >
          <span className={`truncate text-left flex-1 ${selected ? "text-foreground" : "text-muted-foreground"}`}>
            {selected ? selected.label : placeholder}
          </span>
          <CaretUpDown size={12} className="opacity-50 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[240px] max-h-[360px] overflow-hidden flex flex-col border hairline shadow-lg rounded-sm"
        data-testid={testId ? `${testId}-popover` : undefined}
      >
        <div className="relative border-b hairline">
          <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-myb-500" />
          <input
            data-testid={testId ? `${testId}-search` : undefined}
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Type to search…"
            className="w-full pl-8 pr-7 py-2 text-xs outline-none bg-white"
          />
          {q && (
            <button
              type="button"
              onClick={() => setQ("")}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-myb-500 p-0.5"
            ><X size={11} /></button>
          )}
        </div>
        <div className="overflow-y-auto scrollbar-thin flex-1 py-1">
          {allLabel != null && (
            <button
              type="button"
              onClick={() => { onChange(""); setOpen(false); }}
              className={`w-full text-left text-xs px-3 py-2 flex items-center gap-2 hover:bg-myb-50 transition-colors ${!value ? "text-myb-500 font-semibold" : "text-muted-foreground"}`}
            >
              <Check size={12} className={!value ? "opacity-100" : "opacity-0"} />
              {allLabel}
            </button>
          )}
          {list.length === 0 && (
            <div className="px-3 py-6 text-center text-xs text-muted-foreground italic">{emptyText}</div>
          )}
          {list.map((o) => {
            const active = String(o.value) === String(value);
            return (
              <button
                key={o.value}
                type="button"
                data-testid={testId ? `${testId}-opt-${o.value}` : undefined}
                onClick={() => { onChange(o.value); setOpen(false); }}
                className={`w-full text-left text-xs px-3 py-2 flex items-start gap-2 hover:bg-myb-50 transition-colors ${active ? "bg-myb-50" : ""}`}
              >
                <Check size={12} className={`mt-0.5 shrink-0 ${active ? "opacity-100 text-myb-500" : "opacity-0"}`} />
                <span className="flex-1 min-w-0">
                  <div className={`truncate ${active ? "text-myb-500 font-semibold" : "text-foreground"}`}>{o.label}</div>
                  {o.sub && <div className="text-[10px] text-muted-foreground truncate">{o.sub}</div>}
                </span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default SearchSelect;
